<template>
  <div id="crew-basic-info">
    <detail-content-layout
      label="船員基本情報"
      :hide-on-button-click="isEditMode"
      :on-button-label="isEditMode ? '' : '編集する'"
      :on-button-click="handleEditClick"
    >
      <template #actionArea v-if="!isEditMode">
        <Button
          type="border"
          icon="icon-edit-2"
          colorType="normal"
          @click="handleEditClick"
          label="編集する"
        />
      </template>
      <CrewBasicInfoForm
        ref="crewBasicInfoForm"
        :isEditMode="isEditMode"
        :name.sync="name"
        :nameKana.sync="nameKana"
        :availableRoles.sync="availableRoles"
        :affiliatedCompanies.sync="affiliatedCompanies"
        :joiningDate.sync="joiningDate"
        :retirementDate.sync="retirementDate"
        :employeeNumber.sync="employeeNumber"
        :unionMember.sync="unionMember"
        :emailAddress.sync="emailAddress"
        :phoneNumber.sync="phoneNumber"
        :birthDate.sync="birthDate"
        :postalCode.sync="postalCode"
        :prefecture.sync="prefecture"
        :address.sync="address"
        :memo.sync="memo"
        :roleOptions="crewRoles"
      />
      <div class="flex gap-x-4 justify-center" v-if="isEditMode">
        <Button
          type="filled"
          colorType="cancel"
          label="キャンセル"
          @click="cancelEdit"
        />
        <Button
          type="filled"
          colorType="normal"
          @click="save"
          label="保存する"
        />
      </div>
    </detail-content-layout>
  </div>
</template>

<script>
import { CrewBasicInfo } from "../domain/CrewBasicInfo.js";
import Button from "../components/Button.vue";
import CrewBasicInfoForm from "./CrewBasicInfoForm.vue";
import DetailContentLayout from "../components/DetailContentLayout.vue";
import { ColorMixin } from "@/mixins";
import { EnterpriseCrewsService } from "../service/CrewBasicInfoService.js";
import { isPermanentEmployee } from "../constants/EmploymentStatus.js";

export default {
  name: "CrewBasicInfo",
  mixins: [ColorMixin],
  props: {
    crewBasicInfo: {
      type: CrewBasicInfo,
    },
    crewRoles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isEditMode: false,

      name: "",
      nameKana: "",
      availableRoles: [],
      affiliatedCompanies: [],
      joiningDate: "",
      retirementDate: "",
      employeeNumber: "",
      unionMember: false,
      emailAddress: "",
      phoneNumber: "",
      birthDate: "",
      postalCode: "",
      prefecture: "",
      address: "",
      memo: "",
    };
  },
  components: {
    Button,
    CrewBasicInfoForm,
    DetailContentLayout,
  },
  watch: {
    isEditMode(newValue) {
      // 編集中の状態を親に通知
      this.$emit("edit-mode-change", newValue);
    },
  },
  methods: {
    handleEditClick() {
      this.isEditMode = true;
    },
    cancelEdit() {
      // エラーをクリア
      if (this.$refs.crewBasicInfoForm) {
        this.$refs.crewBasicInfoForm.clearErrors();
      }
      this.initializeData();
      this.isEditMode = false;
    },
    save() {
      // バリデーションを実行
      const isValid = this.$refs.crewBasicInfoForm.validate();
      if (!isValid) {
        // エラーがある場合は保存しない
        this.$vs.notify({
          title: "バリデーションエラー",
          text: "入力内容に誤りがあります。各項目を確認してください。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
        return;
      }
      this.update();
    },
    initializeData() {
      console.log(this.crewBasicInfo);
      this.name = this.crewBasicInfo.name;
      this.nameKana = this.crewBasicInfo.name_kana;
      this.availableRoles = this.crewBasicInfo.available_roles;
      this.affiliatedCompanies = this.crewBasicInfo.affiliated_companies;
      this.joiningDate = this.crewBasicInfo.joining_date;
      this.retirementDate = this.crewBasicInfo.retirement_date;
      this.employeeNumber = this.crewBasicInfo.employee_number;
      this.unionMember = this.crewBasicInfo.union_member;
      this.emailAddress = this.crewBasicInfo.email_address;
      this.phoneNumber = this.crewBasicInfo.phone_number;
      this.birthDate = this.crewBasicInfo.birth_date;
      this.postalCode = this.crewBasicInfo.postal_code;
      this.prefecture = this.crewBasicInfo.prefecture;
      this.address = this.crewBasicInfo.address;
      this.memo = this.crewBasicInfo.memo;
    },
    async update() {
      // affiliated_companiesからend_dateがnullの場合は除外
      // 社員区分が「社員」の場合は所属企業名をnullにする
      const affiliatedCompanies = (this.affiliatedCompanies || []).map(
        company => {
          const companyData = { ...company };
          // 社員区分が「社員」の場合は所属企業名をnullにする
          if (isPermanentEmployee(companyData.status)) {
            companyData.name = null;
          }
          return companyData;
        }
      );

      const payload = {
        name: this.name,
        name_kana: this.nameKana,
        available_roles: this.availableRoles,
        affiliated_companies: affiliatedCompanies,
        joining_date: this.joiningDate,
        retirement_date: this.retirementDate,
        employee_number: this.employeeNumber,
        union_member: this.unionMember,
        email_address: this.emailAddress,
        phone_number: this.phoneNumber,
        birth_date: this.birthDate,
        postal_code: this.postalCode,
        prefecture: this.prefecture,
        address: this.address,
        memo: this.memo,
      };
      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        await EnterpriseCrewsService.updateById(this.crewBasicInfo.id, payload);
        this.isEditMode = false;
        this.$vs.notify({
          title: "船員基本情報編集",
          text: "船員基本情報を更新しました",
          color: "success",
          icon: "done",
        });
        // 船員基本情報を更新に成功したら、親のコンポーネントで船員基本情報を再取得
        this.$emit("refresh");
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: "船員基本情報編集",
          text: "船員基本情報を更新できませんでした",
          color: "danger",
          icon: "done",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },
  },
  created() {
    this.initializeData();
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";
</style>
